"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useOnboarding } from "@/onboarding/OnboardingContext";
import { saveDataPools } from "@/services/dataRetentionService";
import { resolveSystemIds } from "@/app/pages/data-retention-schedule/assetLinkingUtil";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const SUGGESTED_POOLS = [
  { id: "crm", name: "CRM Data Pool", reason: "Based on CRM + Support integrations" },
  { id: "billing", name: "Billing Data Pool", reason: "Based on accounting + payments integrations" },
  { id: "people", name: "People/HR Data Pool", reason: "Based on HR + payroll systems" },
  { id: "product", name: "Product Analytics Pool", reason: "Based on analytics vendors" },
];

export default function ModalDataPoolsSuggestExample() {
  const [open, setOpen] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState<string[]>(SUGGESTED_POOLS.map(t => t.id));
  const [pools, setPools] = React.useState(SUGGESTED_POOLS);
  const [poolMeta, setPoolMeta] = React.useState<Record<string, { systems: string[]; suppliers: string[]; labels: string[]; dataTypes: string[] }>>({});
  const [saving, setSaving] = React.useState(false);
  const { setStatus, openStep, closeActive, goBack } = useOnboarding();

  const filtered = React.useMemo(() => pools.filter(t => t.name.toLowerCase().includes(query.toLowerCase())), [query, pools]);
  const toggle = (id: string) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const allShownSelected = filtered.length > 0 && filtered.every(t => selected.includes(t.id));
  const toggleAll = () => {
    if (allShownSelected) {
      setSelected(prev => prev.filter(id => !filtered.some(t => t.id === id)));
    } else {
      setSelected(prev => Array.from(new Set([...prev, ...filtered.map(t => t.id)])));
    }
  };

  const handleSave = async () => {
      if (selected.length === 0) {
          toast.error("Please select at least one data pool");
          return;
      }
      setSaving(true);
      try {
          const companyID = (window as any).companyID;
          if (!companyID) throw new Error("Company ID not found");

          const selectedItems = pools.filter(p => selected.includes(p.id));
          
          // Convert system names to IDs for each pool
          const entities = await Promise.all(selectedItems.map(async p => {
              const systemNames = poolMeta[p.id]?.systems || [];
              const systemIds = await resolveSystemIds(systemNames);
              return {
                  id: p.id,
                  name: p.name,
                  reason: p.reason,
                  dataTypes: poolMeta[p.id]?.dataTypes || [],
                  systems: systemIds,
                  suppliers: poolMeta[p.id]?.suppliers || [],
                  labels: poolMeta[p.id]?.labels || [],
                  status: 'active' as const
              };
          }));
          
          await saveDataPools(companyID, entities);
          
          // Legacy local storage
          try { 
              localStorage.setItem("onboardingAI.accepted.data_pools", JSON.stringify(selectedItems)); 
          } catch { }
          
          setStatus("data_pools", "done");
          setOpen(false);
          openStep("add_processes");
      } catch (error) {
          console.error("Failed to save data pools", error);
          toast.error("Failed to save data pools");
      } finally {
          setSaving(false);
      }
  };

  React.useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('onboardingAI.lastResponse') : null;
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const payload = parsed?.data?.payload;
      const items = payload?.data_pools;
      if (Array.isArray(items) && items.length > 0) {
        const mapped = items.map((d: any) => ({
          id: d.id || d.name,
          name: d.name,
          reason: d.description || (Array.isArray(d.primary_systems) && d.primary_systems.length ? `Systems: ${d.primary_systems.join(', ')}` : 'Suggested'),
        }));
        setPools(mapped);
        setSelected(mapped.map((m: any) => m.id));

        const labelsArr: any[] = Array.isArray(payload?.data_labels) ? payload.data_labels : [];
        const procsArr: any[] = Array.isArray(payload?.processes) ? payload.processes : [];
        const suppliersList: any[] = (payload?.query?.suppliers && Array.isArray(payload.query.suppliers)) ? payload.query.suppliers : (Array.isArray(parsed?.data?.query?.suppliers) ? parsed.data.query.suppliers : []);
        const supplierNameById = new Map<string, string>(suppliersList.map((s: any) => [s.id, s.name]));

        const labelNameById = new Map<string, string>(labelsArr.map((l: any) => [l.id || l.name, l.display_name || l.name]));
        const meta: Record<string, { systems: string[]; suppliers: string[]; labels: string[]; dataTypes: string[] }> = {};
        items.forEach((dp: any) => {
          const systems = (Array.isArray(dp?.primary_systems) ? Array.from(new Set(dp.primary_systems)) : []) as string[];
          // Keep data_label_ids as IDs for the dataTypes field
          const dataTypeIds = (Array.isArray(dp?.data_label_ids) ? Array.from(new Set(dp.data_label_ids)) : []) as string[];
          const labels = dataTypeIds.map((id: string) => labelNameById.get(id)).filter(Boolean) as string[];
          const supplierIds = (Array.from(new Set(
            procsArr
              .filter((pr: any) => Array.isArray(pr?.data_pool_ids) && pr.data_pool_ids.includes(dp.id))
              .flatMap((pr: any) => (Array.isArray(pr?.supplier_ids) ? pr.supplier_ids : []))
          )) as string[]);
          const supplierNames = supplierIds.map((sid: string) => supplierNameById.get(sid)).filter(Boolean) as string[];
          const labelsNormalized = Array.from(new Set(labels)) as string[];
          meta[dp.id] = {
            systems,
            suppliers: supplierNames,
            labels: labelsNormalized,
            dataTypes: dataTypeIds,
          };
        });
        setPoolMeta(meta);
      }
    } catch {}
  }, []);

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Data Pools Suggest</Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) closeActive(); }}>
        <DialogContent className="max-w-7xl! w-7xl! h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col">
            <div className="p-6 pb-4 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent">
              <h2 className="text-3xl font-bold text-foreground">Suggested Data Pools</h2>
              <p className="mt-2 text-muted-foreground">We recommend these pools based on your suppliers and company context.</p>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <Input placeholder="Search data pools..." value={query} onChange={(e) => setQuery(e.target.value)} className="bg-white/10 border-white/20 sm:max-w-sm" />
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <button className="hover:underline" type="button" onClick={toggleAll}>{allShownSelected ? 'Deselect shown' : 'Select shown'}</button>
                  <span>|</span>
                  <span>{selected.length} selected</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map(t => (
                  <label key={t.id} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-4 cursor-pointer">
                    <Checkbox checked={selected.includes(t.id)} onCheckedChange={() => toggle(t.id)} />
                    <div>
                      <div className="text-foreground font-medium">{t.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">{t.reason}</div>
                      {poolMeta[t.id]?.systems && poolMeta[t.id].systems.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1 items-center">
                          <span className="text-[10px] text-muted-foreground mr-1">Primary systems</span>
                          {poolMeta[t.id].systems.map((sys) => (
                            <Badge key={sys} variant="software">{sys}</Badge>
                          ))}
                        </div>
                      )}
                      {poolMeta[t.id]?.labels && poolMeta[t.id].labels.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1 items-center">
                          <span className="text-[10px] text-muted-foreground mr-1">Data labels</span>
                          {poolMeta[t.id].labels.map((label) => (
                            <Badge key={label} variant="policy">{label}</Badge>
                          ))}
                        </div>
                      )}
                      {poolMeta[t.id]?.suppliers && poolMeta[t.id].suppliers.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1 items-center">
                          <span className="text-[10px] text-muted-foreground mr-1">Suppliers</span>
                          {poolMeta[t.id].suppliers.map((s) => (
                            <Badge key={s} variant="service">{s}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              {selected.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selected.map(id => {
                    const t = pools.find(x => x.id === id);
                    if (!t) return null;
                    return <Badge key={id} variant="outline" className="text-xs">{t.name}</Badge>
                  })}
                </div>
              )}
            </div>

            <div className="border-t border-white/10 px-6 py-4 bg-linear-to-t from-primary/5 to-transparent flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setOpen(false); goBack(); }}>Back</Button>
              <Button onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Accept & Next
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
